// Small helpers shared by the medi_consult clinical screens: the prescription
// table (PrescriptionSection), the doctor's medicine catalogue (pages/Medicines)
// and the note-field settings (doctorTabs/PrescriptionFieldsTab).
import { extractErrorMessage } from '../../utils/extractErrorMessage';

// ---------------------------------------------------------------------------
// Server refusals
// ---------------------------------------------------------------------------

// A backend message that is really an untranslated i18n key
// ("doctorMedicine.alreadyExists") or a bare machine code ("FEATURE_DISABLED")
// is not a sentence a doctor can act on — the component's own fallback is
// better than either.
const LOOKS_LIKE_I18N_KEY = /^[A-Za-z][\w-]*(\.[\w-]+)+$/;
const LOOKS_LIKE_CODE = /^[A-Z][A-Z0-9_]+$/;
const AXIOS_GENERIC = /^Request failed with status code \d+$/;

/**
 * The server's own reason for a refusal, as one sentence in the viewer's
 * language. Reads every shape the slices reject with (axios error, response
 * body, `{ message: { en, fr } }`, plain string) through extractErrorMessage,
 * and never returns an object — so it is always safe to hand to a toast.
 */
export const serverReason = (source, fallback, lang = 'en') => {
    const msg = extractErrorMessage(source, '', lang);
    const trimmed = typeof msg === 'string' ? msg.trim() : '';
    if (!trimmed) return fallback;
    if (LOOKS_LIKE_I18N_KEY.test(trimmed) || LOOKS_LIKE_CODE.test(trimmed) || AXIOS_GENERIC.test(trimmed)) {
        return fallback;
    }
    return trimmed;
};

// ---------------------------------------------------------------------------
// Dose counts — quarter steps (¼ ½ ¾ 1 1¼ …)
// ---------------------------------------------------------------------------

export const DOSE_STEP = 0.25;
// The counter's own ceiling. (The server accepts up to 999.75; a stored value
// above 10 is still DISPLAYED as it is — only the + button stops here.)
export const MAX_DOSE = 10;

/**
 * A stored dose count as a number on the quarter grid. The column is DECIMAL,
 * which Postgres/Sequelize hands back as a string ("0.50"), so every read goes
 * through here.
 */
export const toDose = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n / DOSE_STEP) * DOSE_STEP;
};

const FRACTIONS = { 0.25: '¼', 0.5: '½', 0.75: '¾' };

/** 0 → "0", 0.5 → "½", 1 → "1", 1.25 → "1¼". */
export const formatDose = (value) => {
    const n = toDose(value);
    const whole = Math.floor(n);
    const symbol = FRACTIONS[n - whole] || '';
    if (!symbol) return String(whole);
    return whole === 0 ? symbol : `${whole}${symbol}`;
};

// ---------------------------------------------------------------------------
// Free text
// ---------------------------------------------------------------------------

// Everything printable is allowed — digits, slashes, %, °, doses, BP, dates —
// except the two angle brackets, and control characters other than newline,
// carriage return and tab.
// eslint-disable-next-line no-control-regex
const DISALLOWED_TEXT = /[<>\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;

export const cleanClinicalText = (value = '') => String(value).replace(DISALLOWED_TEXT, '');
